/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { type ModelSignal, type WritableSignal, computed, effect, signal } from "@angular/core";
import { v4 as uuidv4 } from "uuid";
import type {
	CalendarCell,
	CalendarDay,
	CalendarEvent,
	DatepickerDisabledDates,
} from "../../widgets/base/datepicker/datepicker.component";

export abstract class CbCalendar {
	public readonly months: string[] = [
		"COMMON.JANUARY",
		"COMMON.FEBRUARY",
		"COMMON.MARCH",
		"COMMON.APRIL",
		"COMMON.MAY",
		"COMMON.JUNE",
		"COMMON.JULY",
		"COMMON.AUGUST",
		"COMMON.SEPTEMBER",
		"COMMON.OCTOBER",
		"COMMON.NOVEMBER",
		"COMMON.DECEMBER",
	];

	/* protected readonly currentDay = signal<number>(new Date().getDate());
	protected readonly currentMonth = signal<number>(new Date().getMonth());
	protected readonly currentYear = signal<number>(new Date().getFullYear()); */
	protected readonly currentDay = computed<number>(() => {
		return this.currentDate().getDate();
	});
	protected readonly currentMonth = computed<number>(() => {
		return this.currentDate().getMonth();
	});
	protected readonly currentYear = computed<number>(() => {
		return this.currentDate().getFullYear();
	});

	public readonly calendarGrid = signal<CalendarCell[]>([]);
	public readonly calendarPreview = signal<CalendarDay[]>([]);
	public readonly calendarPreviewPast = signal<CalendarDay[]>([]);

	public prevMonthsWithNotifications = false;
	public nextMonthsWithNotifications = false;

	protected events: WritableSignal<CalendarEvent[]> = signal([]);

	protected minDate: WritableSignal<Date | undefined> = signal(undefined);
	protected maxDate: WritableSignal<Date | undefined> = signal(undefined);
	protected disabledDates: WritableSignal<DatepickerDisabledDates | undefined> =
		signal(undefined);
	public disabledText?: string;
	public isDisabledSelectable?: boolean = false;

	protected readonly selectedDates = signal<Date[]>([]);

	protected viewMode = signal<"day" | "week" | "month" | "year" | "multi-year">(
		"month",
	);

	constructor() {
		effect(() => {
			this.calculate();
		});
	}

	public abstract currentDate: ModelSignal<Date>;

	/* public readonly currentDate = computed(() => {
		return new Date(this.currentYear(), this.currentMonth(), this.currentDay());
	}); */

	public readonly isTodayVisible = computed(() => {
		return this.calendarGrid().some((cell) => cell.day.isToday);
	});

	public calculate(): void {
		switch (this.viewMode()) {
			case "day":
				this.calendarGrid.set(this.calculateDailyGrid());
				break;
			case "week":
				this.calendarGrid.set(this.calculateWeeklyGrid());
				break;
			case "month":
				this.calendarGrid.set(this.calculateMontlhyGrid());
				break;
			case "year":
				this.calendarGrid.set(this.calculateYearlyGrid());
				break;
			case "multi-year":
				this.calendarGrid.set(this.calculateMultiYearlyGrid());
				break;
		}
	}

	protected isDayDisabled(d: Date): boolean {
		d.setHours(0, 0, 0, 0);

		if (
			this.disabledDates?.()?.dates?.find((date) => this.isSameDay(date, d))
		) {
			return true;
		}
		// Check if the year is disabled
		if (
			this.disabledDates?.()?.years &&
			this.disabledDates?.()?.years?.indexOf(d.getFullYear()) !== -1
		) {
			return true;
		}

		// Check if the month is disabled
		if (
			this.disabledDates?.()?.months &&
			this.disabledDates?.()?.months?.indexOf(d.getMonth()) !== -1
		) {
			return true;
		}

		// Check if the day is disabled by week day
		if (
			this.disabledDates?.()?.weekdays &&
			this.disabledDates?.()?.weekdays?.indexOf(d.getDay()) !== -1
		) {
			return true;
		}

		// Check if the day is disabled by date range
		if (
			this.disabledDates?.()?.periods?.find(
				(period) =>
					d.getTime() >= period.start.getTime() &&
					d.getTime() <= period.end.getTime(),
			)
		) {
			return true;
		}

		// Then check if the day is disabled by date
		if (
			this.disabledDates?.()?.dates?.find((date) => this.isSameDay(date, d))
		) {
			return true;
		}

		this.minDate()?.setHours(0, 0, 0, 0);
		const minDate = this.minDate();
		if (minDate && d < minDate) {
			return true;
		}

		this.maxDate()?.setHours(0, 0, 0, 0);
		const maxDate = this.maxDate();
		if (maxDate && d > maxDate) {
			return true;
		}

		return false;
	}

	protected isSameDay(d1: Date, d2: Date): boolean {
		return this.isSameMonth(d1, d2) && d1.getDate() === d2.getDate();
	}

	protected isBetweenDateRange(range: [Date, Date], date: Date): boolean {
		const start = new Date(range[0]).setHours(0, 0, 0, 0);
		const end = new Date(range[1]).setHours(23, 59, 59, 999);

		return date.getTime() >= start && date.getTime() <= end;
	}

	protected isSameMonth(d1: Date, d2: Date): boolean {
		return this.isSameYear(d1, d2) && d1.getMonth() === d2.getMonth();
	}

	protected isSameYear(d1: Date, d2: Date): boolean {
		return d1.getFullYear() === d2.getFullYear();
	}

	public calculateMontlhyGrid(): CalendarCell[] {
		const days: CalendarCell[] = [];
		const firstDay = new Date(this.currentYear(), this.currentMonth(), 1);
		firstDay.setDate(1);
		const lastDay = new Date(firstDay);
		lastDay.setMonth(firstDay.getMonth() + 1);
		lastDay.setDate(0);

		const firstDayOfWeek = firstDay.getDay() === 0 ? 7 : firstDay.getDay(); // convert Sunday = 0 to Sunday = 7
		const lastDayOfWeek = lastDay.getDay() === 0 ? 7 : lastDay.getDay(); // convert Sunday = 0 to Sunday = 7

		const firstDate = new Date(firstDay);
		firstDate.setDate(firstDay.getDate() + 1 - firstDayOfWeek);

		const lastDate = new Date(lastDay);
		lastDate.setDate(lastDay.getDate() + (7 - lastDayOfWeek)); // get Sunday = 7

		const today = new Date();

		const d = new Date(firstDate);
		while (d.getTime() <= lastDate.getTime()) {
			const events = this.events()
				? this.events().filter((e) =>
					this.isBetweenDateRange([e.startDate, e.endDate], d),
				)
				: [];

			days.push({
				id: uuidv4(),
				isCurrentMonth: d.getMonth() === this.currentMonth(),
				day: {
					date: new Date(d),
					isToday: this.isSameDay(d, today),
					isSelected: this.isDateSelected(d),
					events: events,
					isDisabled: this.isDayDisabled(d),
				},
			});

			d.setDate(d.getDate() + 1);
		}

		return days;
	}

	protected calculateWeeklyGrid(): CalendarCell[] {
		const days: CalendarCell[] = [];
		const baseDate = new Date(
			this.currentYear(),
			this.currentMonth(),
			this.currentDay(),
		);

		const startOfWeek = new Date(baseDate);
		const dow = startOfWeek.getDay() === 0 ? 7 : startOfWeek.getDay();
		startOfWeek.setDate(startOfWeek.getDate() + 1 - dow);

		for (let i = 0; i < 7; i++) {
			const current = new Date(startOfWeek);
			current.setDate(startOfWeek.getDate() + i);
			const events = this.events()
				?.filter((e) =>
					this.isBetweenDateRange([e.startDate, e.endDate], new Date(current)),
				)
				.sort((d1, d2) => {
					return d1.startDate.getTime() - d2.startDate.getTime();
				});
			days.push({
				id: uuidv4(),
				isCurrentMonth: current.getMonth() === this.currentMonth(),
				day: {
					date: new Date(current),
					isToday: this.isSameDay(current, new Date()),
					isSelected: this.isDateSelected(current),
					events,
					isDisabled: this.isDayDisabled(current),
				},
			});
		}

		return days;
	}

	protected calculateDailyGrid(): CalendarCell[] {
		const days: CalendarCell[] = [];
		const baseDate = new Date(
			this.currentYear(),
			this.currentMonth(),
			this.currentDay(),
		);

		const events = this.events()
			?.filter((e) =>
				this.isBetweenDateRange([e.startDate, e.endDate], baseDate),
			)
			.sort((d1, d2) => {
				return d1.startDate.getTime() - d2.startDate.getTime();
			});
		days.push({
			id: uuidv4(),
			isCurrentMonth: true,
			day: {
				date: new Date(baseDate),
				isToday: this.isSameDay(baseDate, new Date()),
				isSelected: this.isDateSelected(baseDate),
				events,
				isDisabled: this.isDayDisabled(baseDate),
			},
		});

		return days;
	}

	protected calculateYearlyGrid(): CalendarCell[] {
		const months: CalendarCell[] = [];
		const year = this.currentYear();
		const today = new Date();
		for (let i = 0; i < 12; i++) {
			const cellDate = new Date(year, i, 1);
			months.push({
				id: uuidv4(),
				isCurrentMonth: i === this.currentMonth(),
				day: {
					date: cellDate,
					isToday: this.isSameMonth(cellDate, today),
					isSelected: this.isMonthSelected(cellDate),
					events: [],
					isDisabled: this.isDayDisabled(cellDate),
				},
			});
		}
		return months;
	}

	protected calculateMultiYearlyGrid(): CalendarCell[] {
		const startYear = this.currentYear() - (this.currentYear() % 10);
		const cells: CalendarCell[] = [];
		const today = new Date();
		for (let i = 0; i < 10; i++) {
			const cellYear = startYear + i;
			const cellDate = new Date(cellYear, 0, 1);
			cells.push({
				id: uuidv4(),
				isCurrentMonth: false,
				day: {
					date: cellDate,
					isToday: this.isSameYear(cellDate, today),
					isSelected: this.isYearSelected(cellDate),
					events: [],
					isDisabled: this.isDayDisabled(cellDate),
				},
			});
		}
		return cells;
	}

	protected isDateSelected(d: Date): boolean {
		return this.selectedDates().some((date) => this.isSameDay(date, d));
	}

	protected isMonthSelected(d: Date): boolean {
		return this.selectedDates().some((date) => this.isSameMonth(date, d));
	}

	protected isYearSelected(d: Date): boolean {
		return this.selectedDates().some((date) => this.isSameYear(date, d));
	}

	public next(): void {
		const currentDate = new Date(
			this.currentYear(),
			this.currentMonth(),
			this.currentDay(),
		);

		switch (this.viewMode()) {
			case "day":
				currentDate.setDate(currentDate.getDate() + 1);
				break;
			case "week":
				currentDate.setDate(currentDate.getDate() + 7);
				break;
			case "month":
				currentDate.setDate(1);
				currentDate.setMonth(currentDate.getMonth() + 1);
				break;
			case "year":
				currentDate.setFullYear(currentDate.getFullYear() + 1);
				break;
			case "multi-year":
				currentDate.setFullYear(currentDate.getFullYear() + 10);
				break;
		}

		/* this.currentDay.set(currentDate.getDate());
		this.currentMonth.set(currentDate.getMonth());
		this.currentYear.set(currentDate.getFullYear()); */
		this.currentDate.set(currentDate);
	}

	public previous(): void {
		const currentDate = new Date(
			this.currentYear(),
			this.currentMonth(),
			this.currentDay(),
		);

		switch (this.viewMode()) {
			case "day":
				currentDate.setDate(currentDate.getDate() - 1);
				break;
			case "week":
				currentDate.setDate(currentDate.getDate() - 7);
				break;
			case "month":
				currentDate.setMonth(currentDate.getMonth() - 1);
				break;
			case "year":
				currentDate.setFullYear(currentDate.getFullYear() - 1);
				break;
			case "multi-year":
				currentDate.setFullYear(currentDate.getFullYear() - 10);
				break;
		}

		/* this.currentDay.set(currentDate.getDate());
		this.currentMonth.set(currentDate.getMonth());
		this.currentYear.set(currentDate.getFullYear()); */
		this.currentDate.set(currentDate);
	}

	public setMonth(month: number): void {
		const date = this.currentDate();
		date.setMonth(month);
		this.currentDate.set(date);
	}

	public setYear(year: number): void {
		const date = this.currentDate();
		date.setFullYear(year);
		this.currentDate.set(date);
	}
}
